// Public, UNAUTHENTICATED induction/sign-in submission surface (open-link / QR).
// Strictly scoped to a single issue's submission token — no app shell, no other data.
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, PenLine, Upload, ShieldCheck, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full h-11 px-3 rounded-lg border border-[#E5E0DA] bg-white text-[15px] focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]";

export default function PublicSubmitPage() {
    const { token } = useParams();
    const [view, setView] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    const [name, setName] = useState("");
    const [answers, setAnswers] = useState({});
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const sigRef = useRef(null);
    const drawing = useRef(false);
    const [hasSig, setHasSig] = useState(false);

    useEffect(() => {
        axios.get(`${API}/issues/token/${token}`)
            .then((r) => setView(r.data))
            .catch((e) => setError(e.response?.status === 404 ? "This link is invalid or has expired." : "Could not load this submission link."))
            .finally(() => setLoading(false));
    }, [token]);

    // simple signature pad
    function startDraw(e) { drawing.current = true; draw(e); }
    function endDraw() { drawing.current = false; const c = sigRef.current; if (c) c.getContext("2d").beginPath(); }
    function draw(e) {
        if (!drawing.current) return;
        const c = sigRef.current; const rect = c.getBoundingClientRect();
        const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
        const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
        const ctx = c.getContext("2d");
        ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#1F1F1F";
        ctx.lineTo(x, y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(x, y);
        setHasSig(true);
    }
    function clearSig() { const c = sigRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasSig(false); }

    async function submit() {
        if (!name.trim()) { setError("Please enter your name."); return; }
        setBusy(true); setError(null);
        try {
            const fd = new FormData();
            fd.append("name", name.trim());
            if (hasSig && sigRef.current) fd.append("signature", sigRef.current.toDataURL("image/png"));
            if (view.completion_mode === "upload" && file) fd.append("file", file);
            if (view.completion_mode === "form") fd.append("form_answers", JSON.stringify(answers));
            await axios.post(`${API}/issues/token/${token}/submit`, fd);
            setDone(true);
        } catch (e) {
            setError(e.response?.data?.detail || "Submission failed. Please try again.");
        }
        setBusy(false);
    }

    if (loading) return <Center><Loader2 className="animate-spin" size={28} /></Center>;
    if (error && !view) return <Center><div className="text-center"><AlertCircle className="mx-auto mb-2 text-red-500" size={28} /><p className="text-[#6F6A64]">{error}</p></div></Center>;

    if (done) return (
        <Center>
            <div className="text-center max-w-sm" data-testid="public-submit-done">
                <CheckCircle2 className="mx-auto mb-3 text-[#3F7D3A]" size={42} />
                <h1 className="text-xl font-bold text-[#1F1F1F]">Thank you, {name.split(" ")[0]}</h1>
                <p className="text-[#6F6A64] mt-1.5 text-[14px]">Your submission for <b>{view.title}</b> has been recorded.</p>
            </div>
        </Center>
    );

    const accepting = view.accepting;
    const fields = (view.form_template?.sections || []).flatMap((s) => s.fields || []).filter((f) => !["instruction"].includes(f.field_type));

    return (
        <div className="min-h-screen bg-[#F5F3F0] py-8 px-4" data-testid="public-submit-page">
            <div className="max-w-lg mx-auto">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg grid place-items-center text-white font-bold" style={{ background: "var(--brand-accent,#C75A1B)" }}>PR</div>
                    <span className="text-[12px] font-bold uppercase tracking-wider text-[#6F6A64]">Priority Risk</span>
                </div>
                <div className="bg-white rounded-[16px] border shadow-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                    <div className="px-5 py-4 border-b" style={{ borderColor: "#F0EDE8" }}>
                        <h1 className="text-lg font-bold text-[#1F1F1F]">{view.title}</h1>
                        {view.project_name && <div className="text-[12.5px] text-[#9A968F] mt-0.5">{view.project_name}</div>}
                        {view.instructions && <p className="text-[13px] text-[#6F6A64] mt-2">{view.instructions}</p>}
                    </div>
                    {!accepting ? (
                        <div className="p-6 text-center text-[#6F6A64]" data-testid="public-submit-closed">
                            <ShieldCheck className="mx-auto mb-2 text-[#9A968F]" size={28} />
                            Submissions for this link are closed.
                        </div>
                    ) : (
                        <div className="p-5 space-y-4">
                            <label className="block">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Your name</span>
                                <input data-testid="public-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls + " mt-1"} />
                            </label>

                            {view.completion_mode === "upload" && (
                                <label className="block">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] flex items-center gap-1"><Upload size={12} /> Upload file</span>
                                    <input data-testid="public-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 text-sm w-full" />
                                </label>
                            )}

                            {view.completion_mode === "form" && fields.map((f) => (
                                <label key={f.id} className="block">
                                    <span className="text-[12px] font-semibold text-[#1F1F1F]">{f.label}{f.required && <span className="text-red-500"> *</span>}</span>
                                    {f.field_type === "textarea" ? (
                                        <textarea value={answers[f.id] || ""} onChange={(e) => setAnswers({ ...answers, [f.id]: e.target.value })} rows={3} className={inputCls + " h-auto py-2 mt-1"} />
                                    ) : f.field_type === "select" ? (
                                        <select value={answers[f.id] || ""} onChange={(e) => setAnswers({ ...answers, [f.id]: e.target.value })} className={inputCls + " mt-1"}>
                                            <option value="">— Select —</option>
                                            {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    ) : (
                                        <input type={["number", "date"].includes(f.field_type) ? f.field_type : "text"} value={answers[f.id] || ""} onChange={(e) => setAnswers({ ...answers, [f.id]: e.target.value })} className={inputCls + " mt-1"} />
                                    )}
                                </label>
                            ))}

                            <div>
                                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] flex items-center gap-1"><PenLine size={12} /> Signature</span>
                                <canvas ref={sigRef} width={440} height={130} data-testid="public-signature"
                                    onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                                    onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                                    className="mt-1 w-full rounded-lg border bg-[#FCFBF9] touch-none" style={{ borderColor: "#E5E0DA" }} />
                                <button onClick={clearSig} className="text-[11px] text-[#9A968F] hover:text-[#1F1F1F] mt-1">Clear</button>
                            </div>

                            {error && <div className="text-[12.5px] text-red-600 flex items-center gap-1"><AlertCircle size={13} /> {error}</div>}
                            <button data-testid="public-submit" onClick={submit} disabled={busy}
                                className="w-full h-11 rounded-lg text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                {busy ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} Submit
                            </button>
                        </div>
                    )}
                </div>
                <p className="text-center text-[11px] text-[#9A968F] mt-4">Secured submission · Priority Risk</p>
            </div>
        </div>
    );
}

function Center({ children }) {
    return <div className="min-h-screen grid place-items-center bg-[#F5F3F0] p-4">{children}</div>;
}
